import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Box,
  Card,
  ElementContainer,
  PARTNER_USER_ROLE_OPTIONS,
  Select,
  SideDrawer,
  TextField,
  withStyles,
} from 'vopay';
import { Button } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useCreateUser from '@/hooks/useApi/useCreateUser';
import styles from './CreateUserDrawer.module.scss';

const cn = withStyles(styles);

type CreateUserDrawerProps = {
  isOpen: boolean;
  existingUsernames: string[];
  /** The partner account whose users these are, when not the logged-in partner's own. */
  partnerAccountID?: string;
  onClose: () => void;
  onUserCreated?: () => void;
};

export default function CreateUserDrawer({
  isOpen,
  existingUsernames,
  partnerAccountID,
  onClose,
  onUserCreated,
}: CreateUserDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List.CreateUserDrawer' });
  const toast = useToast();
  const { submitting, createUser } = useCreateUser();
  const [emailAddress, setEmailAddress] = useState('');
  // TODO: role stays a plain string (not a narrowed 'Admin' | 'Support' |
  // 'Viewer' union) because it's passed as CreateUserRequest.Role — a type
  // defined once in component-library and also consumed by client-dashboard's
  // CreateUserContainer.tsx (still routed), where Role holds Account Portal's
  // dynamic/custom role names. Narrowing it here would narrow it for that
  // consumer too and be inaccurate there. Revisit once ID-13192's shared
  // component work settles this.
  const [role, setRole] = useState('');

  const roleOptions = useMemo(
    () => Object.entries(PARTNER_USER_ROLE_OPTIONS).map(([value, label]) => ({ value, label })),
    [],
  );

  const { isEmailValid, isDuplicateEmail, emailInlineError } = useMemo(() => {
    const valid = Yup.string().email().required().isValidSync(emailAddress);
    const duplicate = existingUsernames.some((existing) => existing.toLowerCase() === emailAddress.toLowerCase());

    const inlineError = duplicate
      ? t('duplicateEmail', { defaultValue: 'This email is already used. Please use a different email.' })
      : undefined;

    return { isEmailValid: valid, isDuplicateEmail: duplicate, emailInlineError: inlineError };
  }, [emailAddress, existingUsernames, t]);

  function handleClose() {
    setEmailAddress('');
    setRole('');
    onClose();
  }

  async function handleCreate() {
    try {
      await createUser({ EmailAddress: emailAddress, SendEmail: true, Role: role, PartnerAccountID: partnerAccountID });
      toast.success(t('successMessage', { defaultValue: 'User was successfully created.' }));
      onUserCreated?.();
      handleClose();
    } catch {
      toast.neutral(t('failMessage', { defaultValue: 'Unable to create the user.' }));
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title={t('title', { defaultValue: 'Create User' })}
      footer={
        <Box className={cn('footer')}>
          <Button
            colorScheme="primary"
            disabled={!isEmailValid || isDuplicateEmail || !role || submitting}
            onClick={handleCreate}
          >
            {t('createButton', { defaultValue: 'Create User' })}
          </Button>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <Box flx="col" gapSize="16px">
            <ElementContainer
              label={t('emailAddress', { defaultValue: 'Email Address' })}
              required
              isBold={false}
              padding="0 0 6px"
              htmlFor="newUserEmail"
              input={
                <TextField
                  name="newUserEmail"
                  placeholder={t('emailPlaceholder', { defaultValue: 'alex.rogers@acme.com' })}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  variant="outlineShadow"
                  error={emailInlineError}
                  errorFitContent
                />
              }
            />

            <ElementContainer
              label={t('role', { defaultValue: 'Role' })}
              required
              isBold={false}
              padding="0 0 6px"
              htmlFor="newUserRole"
              input={
                <Select
                  version="2"
                  name="newUserRole"
                  options={roleOptions}
                  value={role}
                  placeholder={t('rolePlaceholder', { defaultValue: 'Select' })}
                  sort={false}
                  onChange={(e) => setRole(e.target.value)}
                />
              }
            />
          </Box>
        </Card>
      </div>
    </SideDrawer>
  );
}
